import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Users, Loader2, Shield, Eye, UserPlus, X } from 'lucide-react'
import api from '../../services/api'
import { useAuth, userRoles } from '../../hooks/useAuth'
import { useProjects } from '../../hooks/useProjects'
import { useTranslation } from 'react-i18next'

/**
 * My Team — a per-project roster of the people on each of the current user's projects.
 *  • PM (owner) / Admin: can add another staff member (PM/Finance) as a co-manager
 *    (write access) or a view-only member, and flip an existing member between the two.
 *  • Finance / Consultant: read-only view of which teams/projects they're part of.
 */
export default function MyTeam() {
  const { t } = useTranslation('people')
  const { user } = useAuth()
  const { projects: allProjects, loading: projectsLoading } = useProjects()
  const [membersByProject, setMembersByProject] = useState({})   // projectId -> [members]
  const [staff, setStaff] = useState([])                         // PM/Finance users for the picker
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(null)                         // `${projectId}:${userId}` in-flight
  const [error, setError] = useState(null)
  // Add-member form state, keyed by project id
  const [addOpen, setAddOpen] = useState(null)                   // projectId whose add-row is open
  const [addUserId, setAddUserId] = useState('')
  const [addLevel, setAddLevel] = useState('view')              // 'view' | 'manage'

  const pmId = (p) => p.project_manager_id ?? p.projectManagerId

  const loadMembers = async () => {
    if (!allProjects?.length) { setLoading(false); return }
    setLoading(true)
    try {
      const entries = await Promise.all(allProjects.map(async (p) => {
        try {
          const r = await api.get(`/projects/${p.id}/members`)
          return [p.id, r.data?.members || []]
        } catch { return [p.id, []] }
      }))
      setMembersByProject(Object.fromEntries(entries))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (projectsLoading) return
    loadMembers()
    // Staff picker (PM/Finance) — only needed for managers.
    if (user?.role === userRoles.ADMIN || user?.role === userRoles.PROJECT_MANAGER) {
      api.get('/users/').then(r => {
        const s = (r.data || []).filter(u => [userRoles.PROJECT_MANAGER, userRoles.FINANCE].includes(u.role))
        setStaff(s)
      }).catch(() => {})
    }
  }, [projectsLoading, allProjects])

  // Scope per role: PM/Consultant lists arrive already filtered by useProjects. Finance
  // gets ALL projects from that hook (for budget pages), so here keep only the projects
  // where they are actually on the team. Admin (system authority) sees every team.
  const projects = useMemo(() => {
    if (user?.role !== userRoles.FINANCE) return allProjects
    return (allProjects || []).filter(p =>
      (membersByProject[p.id] || []).some(m => m.user_id === user.id)
    )
  }, [allProjects, membersByProject, user])

  const canManageProject = (p) =>
    user?.role === userRoles.ADMIN || pmId(p) === user?.id

  const setAccess = async (projectId, userId, can_manage) => {
    setBusy(`${projectId}:${userId}`); setError(null)
    try {
      await api.post(`/projects/${projectId}/delegate`, { user_id: userId, can_manage })
      const r = await api.get(`/projects/${projectId}/members`)
      setMembersByProject(prev => ({ ...prev, [projectId]: r.data?.members || [] }))
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not update access.')
    } finally {
      setBusy(null)
    }
  }

  const addMember = async (projectId) => {
    if (!addUserId) return
    await setAccess(projectId, parseInt(addUserId), addLevel === 'manage')
    setAddOpen(null); setAddUserId(''); setAddLevel('view')
  }

  if (projectsLoading || loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Users className="w-6 h-6 text-blue-600" /> My Team</h1>
        <p className="text-sm text-gray-500 mt-1">
          {user?.role === userRoles.PROJECT_MANAGER || user?.role === userRoles.ADMIN
            ? 'The people on each of your projects. Add another manager as a co-manager (write access) or view-only, e.g. to cover a project for a while.'
            : 'The teams and projects you are part of.'}
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5 rounded-lg flex items-center justify-between">
          {error}
          <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900"><X className="w-4 h-4" /></button>
        </div>
      )}

      {(!projects || projects.length === 0) && (
        <div className="text-center py-14 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">{t('not_in_team')}</p>
        </div>
      )}

      {(projects || []).map(p => {
        const members = membersByProject[p.id] || []
        const manage = canManageProject(p)
        const memberIds = new Set(members.map(m => m.user_id))
        const pickable = staff.filter(u => !memberIds.has(u.id) && u.id !== pmId(p))
        return (
          <div key={p.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <Link to={`/projects/${p.id}`} className="text-sm font-semibold text-gray-800 hover:text-blue-700 truncate block">{p.name}</Link>
                <p className="text-[11px] text-gray-500">{p.project_number || p.projectNumber || `#${p.id}`} · {p.status}</p>
              </div>
              {manage && (
                <button
                  onClick={() => { setAddOpen(addOpen === p.id ? null : p.id); setAddUserId(''); setAddLevel('view') }}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 border border-blue-200 bg-blue-50 rounded-lg hover:bg-blue-100 shrink-0"
                ><UserPlus className="w-3.5 h-3.5" /> Add manager</button>
              )}
            </div>

            {manage && addOpen === p.id && (
              <div className="px-4 py-3 border-b border-gray-100 bg-blue-50/40 flex flex-wrap items-center gap-2">
                <select value={addUserId} onChange={e => setAddUserId(e.target.value)} className="text-sm border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white min-w-[220px]">
                  <option value="">— select a manager —</option>
                  {pickable.map(u => <option key={u.id} value={u.id}>{u.full_name} · {u.role}</option>)}
                </select>
                <select value={addLevel} onChange={e => setAddLevel(e.target.value)} className="text-sm border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white">
                  <option value="view">{t('view_only')}</option>
                  <option value="manage">{t('co_manager_write')}</option>
                </select>
                <button
                  onClick={() => addMember(p.id)}
                  disabled={!addUserId || busy}
                  className="px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >Add</button>
                {pickable.length === 0 && <span className="text-[11px] text-gray-500">{t('all_managers_on')}</span>}
              </div>
            )}

            <div className="divide-y divide-gray-100">
              {members.length === 0 && (
                <p className="px-4 py-3 text-xs text-gray-400 italic">No invited members yet — the owning PM and assigned consultants still have access.</p>
              )}
              {members.map(m => {
                const isOwner = m.user_id === pmId(p)
                return (
                  <div key={m.user_id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-gray-800 truncate">{m.name}</p>
                      <p className="text-[11px] text-gray-500">{m.role}{isOwner ? ' · owner' : ''}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[10px] font-medium border rounded-full px-2 py-0.5 inline-flex items-center gap-1 ${isOwner || m.can_manage ? 'border-green-200 bg-green-50 text-green-700' : 'border-gray-200 bg-gray-50 text-gray-500'}`}>
                        {isOwner ? <><Shield className="w-3 h-3" /> Manager</> : m.can_manage ? <><Shield className="w-3 h-3" /> Co-manager</> : <><Eye className="w-3 h-3" /> View only</>}
                      </span>
                      {manage && !isOwner && (
                        <button
                          onClick={() => setAccess(p.id, m.user_id, !m.can_manage)}
                          disabled={busy === `${p.id}:${m.user_id}`}
                          className="text-xs px-2 py-1 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
                        >
                          {busy === `${p.id}:${m.user_id}` ? '…' : (m.can_manage ? 'Set view-only' : 'Make co-manager')}
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
